import React, { useState } from 'react';
import * as Vlab from 'vlab-frontend-components';
import { phoneMask } from '../../utils/masks';
import { IRatingItemProps } from '../../interface/ratingsInterface';
import { useTranslation } from 'react-i18next';
import { insightsApi } from '../../services/api';

function RatingItem({ 
    rating, 
    className = '',
    backgroundColor = 'bg-white',
    ratingStarColor = "text-[#0166FF]",
    haveIcon = false,
    haveShare = false,
    organizationCode,
    responsive = false
}: IRatingItemProps) {
    const { t } = useTranslation();
    const [isHovered, setIsHovered] = useState(false);
    const [isFilling, setIsFilling] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const dateFormatOptions: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    };

    const date = new Date(rating.rate_at).toLocaleDateString(
        document.documentElement.lang,
        dateFormatOptions
    );

    const renderStars = (rating: number) => {
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            stars.push(
                <span 
                    key={i}
                    className={`text-xl ${i <= rating ? ratingStarColor : "text-[#DDE5EC]"}`}
                >
                    ★
                </span>
            );
        }
        return <div className="flex gap-1 self-start">{stars}</div>;
    };

    const handleShare = async () => {
        setIsFilling(true);
        setIsDownloading(true);
        try {
            const blob = await insightsApi.getRateImage(organizationCode as string, String(rating.id));
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `avaliacao-${rating.id}.png`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setTimeout(() => {
                setIsFilling(false);
                setIsDownloading(false);
            }, 1000);
        } catch (error) {
            setIsFilling(false);
            setIsDownloading(false);
            alert('Erro ao baixar imagem de compartilhamento.');
        }
    };

    const RatingIcon = () => {
        const createTooltip = (IconComponent: any, tooltipKey: string) => (
            <div className="relative group">
                <IconComponent width="16px" height="16px" />
                <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 inline-flex px-1.5 py-1.5 rounded bg-[#8B98A3] text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                    {t(tooltipKey)}
                    <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-[#8B98A3]" />
                </div>
            </div>
        );

        const ratingConfig = {
            super: { component: Vlab.SuperFeedbackIcon, tooltip: 'appEnabled.superFeedbackTooltip' },
            alert: { component: Vlab.AlertIcon, tooltip: 'appEnabled.alertFeedbackTooltip' },
            critical: { component: Vlab.CriticalAlertIcon, tooltip: 'appEnabled.criticalFeedbackTooltip' }
        };

        const config = rating.rate >= 4 ? ratingConfig.super 
                    : rating.rate >= 2 ? ratingConfig.alert 
                    : ratingConfig.critical;

        return createTooltip(config.component, config.tooltip);
    };

    return (
        <div 
            className={`
                ${responsive 
                    ? 'w-[350px] min-w-[350px] lg:min-w-[100%] flex-col lg:flex-row gap-3 lg:gap-6 px-4 py-3 lg:px-6 lg:py-4 lg:w-full mb-6 mt-16 mb-6 lg:my-0' 
                    : 'flex-row gap-6 px-6 py-4 w-full'}
                justify-between flex rounded items-center leading-4
                ${className}
                ${haveShare && (isHovered || isDownloading) ? 'bg-[#ECF2F8]' : backgroundColor}
            `}
            onMouseEnter={() => haveShare && setIsHovered(true)}
            onMouseLeave={() => haveShare && setIsHovered(false)}
        >
            <div className="flex items-center gap-4 w-full lg:w-[200px]">
                <div className="w-[32px] h-[32px] min-w-[32px] rounded-full overflow-hidden flex items-center justify-center bg-[#ECF2F8]">
                    {rating.profile_picture ? (
                        <img 
                            src={rating.profile_picture}
                            alt={rating.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                const img = e.target as HTMLImageElement;
                                e.currentTarget.style.display = 'none';
                                const fallbackDiv = e.currentTarget.parentElement?.querySelector('div:last-child');
                                if (fallbackDiv) fallbackDiv.className = 'block';
                            }}
                        />
                    ) : null}
                    <div className={rating.profile_picture ? 'hidden' : 'block'}>
                        <Vlab.UserIcon width="16px" height="16px" color="#0166FF" />
                    </div>
                </div>
                <div className="m-0 flex-shrink min-w-0">
                    <p className="font-semibold text-[16px] text-[#51525F] overflow-hidden text-ellipsis whitespace-nowrap w-[150px]" title={rating.name}>
                        {rating.name}
                    </p>
                    <p className="text-[10px] w-[120px] mt-1 text-[#8B98A3] leading-[120%] font-normal">
                        {phoneMask(rating.cellphone)}
                    </p>
                </div>
            </div>

            <div className="flex flex-col items-start gap-1 w-full max-w-[100% - 48px]">
                <div className="flex items-center gap-2">
                    {renderStars(rating.rate)} 
                    {haveIcon && RatingIcon()}
                </div>
                {rating.rate_comment && (
                    <p className="w-full max-w-[100% - 48px] text-[#51525F] leading-[120%] lg:leading-[140%] text-[12px] font-normal">
                        {rating.rate_comment}
                    </p>
                )}
            </div>

            <div className={`${responsive  ? 'self-start lg:self-center' : 'self-center'} min-w-[140px] flex gap-6 items-center `}>
                <div className={`${responsive ? 'hidden lg:block' : 'block'} h-[32px] w-[1px] bg-[#DDE5EC]`}/>
                {haveShare && (isHovered || isDownloading) ? (
                <button
                    className="flex px-2 py-1 justify-center items-center rounded-[8px] bg-[#0166FF] relative overflow-hidden"
                    style={{ fontFamily: 'Archivo, sans-serif' }}
                    onClick={handleShare}
                    disabled={isFilling}
                >
                    <span
                        className="absolute left-0 top-0 h-full z-0"
                        style={{
                            width: isFilling ? '100%' : '0%',
                            background: '#0145D5',
                            transition: isFilling ? 'width 2s linear' : 'none',
                        }}
                    />
                    <span className="text-white text-center text-[12px] font-semibold leading-[16px] relative z-10">
                        {t('appEnabled.share')}
                    </span>
                </button>
                ) : (
                    <p className="text-[10px] text-[#8B98A3] text-left lg:text-center leading-[120%] font-normal">
                        {date}
                    </p>
                )}
            </div>
        </div>
    );
}

export default RatingItem;